import React, { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Row, Col } from "react-bootstrap";
import { Info as InfoIcon } from '@mui/icons-material';
import { ListItemIcon, ListItemText, MenuItem, FormControl, InputLabel, Select, FormHelperText, Tooltip } from '@mui/material';
import { useController, useForm, useFormContext } from 'react-hook-form';
import './SelectInput.scss';

// The parent form memoizes option arrays, so this component can skip unrelated re-renders.
function SelectInput(props) {
  const { selectOptions, onChange, name, id, variant, helpText, inputColumnSize, hideEmptyOption, isImageOptions, onBlur, ...textFieldProps } = props;
  const formContext = useFormContext();
  const fallbackForm = useForm();
  const { control } = formContext ?? fallbackForm;

  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control
  });

  const columnSize = inputColumnSize || 12;
  // Keep generated option elements stable while typing in sibling fields.
  const empty = useMemo(() => [<option aria-label="None" value="" key={0} />], []);

  // Mapping options can be noisy for state lists; memoize until options actually change.
  const menuItems = useMemo(() => isImageOptions
    ? selectOptions?.map((item, i) => (
      <MenuItem value={item.value} className="menuItem" key={i}>
        {item.icon && (
          <ListItemIcon className="listItemIcon">
            <item.icon className="svg" />
          </ListItemIcon>
        )}
        <ListItemText className="text" primary={item.label} secondary={item.secondaryText} />
      </MenuItem>
    ))
    : selectOptions?.map((option, key) => (
      <option value={option.value} key={key + 1} disabled={option.disabled}>
        {option.label}
      </option>
    )), [isImageOptions, selectOptions]);

  // Preserve the empty option composition without rebuilding the array every render.
  const allMenuItems = useMemo(() => selectOptions
    ? hideEmptyOption
      ? menuItems
      : empty.concat(menuItems)
    : empty, [empty, hideEmptyOption, menuItems, selectOptions]);

  // Stable handlers let React.memo compare props cheaply across parent updates.
  const handleChange = useCallback((option) => {
    if (!option) field.onChange(null)
    else {
      field.onChange(option.target.value)
      onChange && onChange(option)
    }
  }, [field, onChange]);

  const handleBlur = useCallback((event) => {
    field.onBlur(); // Ensures react-hook-form validation runs
    onBlur && onBlur(event); // Calls parent onBlur if provided
  }, [field, onBlur]);

  return (
    <Row>
      <Col xs={columnSize}>
        <FormControl fullWidth variant={variant || "filled"} error={!!error}>
          <InputLabel htmlFor={id}>{props.label}</InputLabel>
          <Select
            onChange={handleChange}
            onBlur={handleBlur}
            native={!isImageOptions}
            {...textFieldProps}
            inputProps={{ id }}
          >
            {allMenuItems}
          </Select>
          <FormHelperText id={`${id}-helper-text`} aria-describedby={`${id}-helper-text`}>{error?.message}</FormHelperText>
        </FormControl>
      </Col>

      {helpText && (
        <Col xs={2}>
          <div className="d-flex mt-2">
            <Tooltip title={helpText}>
              <InfoIcon color="action" />
            </Tooltip>
          </div>
        </Col>
      )}
    </Row>
  );
}

SelectInput.propTypes = {
  id: PropTypes.string.isRequired,
  selectOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any.isRequired,
      label: PropTypes.any.isRequired,
      disabled: PropTypes.bool,
    })
  ).isRequired,
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  variant: PropTypes.string,
  hideEmptyOption: PropTypes.bool,
  helpText: PropTypes.string,
  inputColumnSize: PropTypes.number,
  isImageOptions: PropTypes.bool,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
};

// Memo keeps selects from re-rendering when a different field changes.
export default memo(SelectInput);
